/**
 * Client-owned timing and limit constants for offline attendance
 * (`specs/offline-attendance-v1/implementation-plan.md`).
 *
 * Everything the client decides on its own — how often it wakes the sync
 * engine, how long a sync lease lives, how it backs off a failed upload — lives
 * here rather than being sprinkled across the services, so each number has one
 * home. Limits ERPat decides per tenant (capture/upload age, clock thresholds,
 * note length, break cap, batch size) arrive in the provisioning policy
 * (`OfflinePolicyV1`) instead; the `*_DEFAULT_*` values below are only the
 * fallbacks used before a policy exists.
 *
 * Import-free on purpose, like the contract module beside it.
 */

/** Most events one `POST sync` may carry (ERPat refuses a bigger batch with 422). */
export const OFFLINE_SYNC_BATCH_MAX = 50;

/** Upload back-off: first retry after 5 s, doubling to a 60 s ceiling (see {@link syncBackoffMs}). */
export const OFFLINE_SYNC_BACKOFF_BASE_MS = 5_000;
export const OFFLINE_SYNC_BACKOFF_MAX_MS = 60_000;

/** The foreground safety-net wake while the app is visible. */
export const OFFLINE_SYNC_PERIODIC_MS = 60_000;

/** One sync owner per scope: an IndexedDB lease lives 30 s and its holder renews it every 10 s. */
export const OFFLINE_SYNC_LEASE_MS = 30_000;
export const OFFLINE_SYNC_LEASE_RENEW_MS = 10_000;

/** A paused engine (auth/permission/protocol/storage) re-checks itself every 15 minutes. */
export const OFFLINE_SYNC_PAUSE_RECHECK_MS = 900_000;

/** Re-provision (grant, policy, confirmed snapshot) when the stored context is older than 30 minutes. */
export const OFFLINE_CONTEXT_REFRESH_MS = 1_800_000;

/** Two captures closer than this are a double tap: the second is refused as busy. */
export const OFFLINE_CAPTURE_MIN_GAP_MS = 2_000;

/** Every offline-attendance request is aborted after 45 s. */
export const OFFLINE_REQUEST_TIMEOUT_MS = 45_000;

/** Settled-accepted rows are pruned 7 days after settlement (once a confirmed snapshot reflects them). */
export const OFFLINE_ACCEPTED_PRUNE_MS = 604_800_000;

/** An app update waits at most 3 s for an in-progress capture/settlement transaction. */
export const OFFLINE_UPDATE_QUIESCE_MAX_MS = 3_000;

/** Fallback clock-jump threshold before a policy is known (policy `clock_jump_block_s`). */
export const OFFLINE_CLOCK_JUMP_DEFAULT_MS = 120_000;

/** Fallback calibration-uncertainty limit before a policy is known (policy `clock_uncertainty_block_s`). */
export const OFFLINE_CLOCK_UNCERTAINTY_DEFAULT_MS = 60_000;

/** ERPat refuses an event more than 5 minutes in its future (`future_event`). */
export const OFFLINE_FUTURE_TOLERANCE_MS = 300_000;

/**
 * A grant is honoured from 5 minutes before its `vfr` (ERPat verifies
 * `occurred_at ≥ (vfr − 300) s`); earlier than that the device clock is
 * treated as unsafe.
 */
export const OFFLINE_GRANT_EARLY_TOLERANCE_MS = 300_000;

/** Readiness warns `sync_failing` once uploads have kept failing for longer than an hour. */
export const OFFLINE_SYNC_FAILING_WARN_MS = 3_600_000;

/**
 * How far past the longest wait the client itself could have scheduled a
 * stored wake time may lie before {@link clockTurnedBack} reads it as a device
 * clock turned back. Small on purpose: the client never writes a retry time, a
 * pause or a lease expiry further ahead than its own longest wait, so the
 * slack only absorbs the few milliseconds between two readings of the clock.
 * A caller whose `now` can go stale across an await widens `longestWaitMs`
 * itself (the sync lease uses two lease lengths).
 */
export const OFFLINE_CLOCK_REWIND_SLACK_MS = 5_000;

/**
 * Whether `at` — a wake time this client stored as "now + a wait of at most
 * `longestWaitMs`" (a retry time, a pause, a sync lease's expiry), all read on
 * the device WALL clock — lies further ahead of `now` than that longest wait
 * plus {@link OFFLINE_CLOCK_REWIND_SLACK_MS}. Nothing the client schedules is
 * ever that far ahead, so the only way to get there is a clock turned back
 * after `at` was written (a manual correction, a network time sync): the wait
 * is to be treated as over. Honouring it would hold the queue for as long as the clock
 * moved — days, past the upload age, after which ERPat refuses the punches as
 * `too_old`. A wait within the bound is never shortened.
 *
 * `false` when any argument is not a finite number (never cut a wait short on
 * a reading that makes no sense); a negative `longestWaitMs` counts as none.
 */
export function clockTurnedBack(at: number, now: number, longestWaitMs: number): boolean {
  if (!Number.isFinite(at) || !Number.isFinite(now) || !Number.isFinite(longestWaitMs)) return false;
  return at - now > Math.max(0, longestWaitMs) + OFFLINE_CLOCK_REWIND_SLACK_MS;
}

/**
 * The wait before upload attempt `attempt` (1-based): the ceiling starts at
 * 5 s and doubles per attempt up to 60 s, and the wait is jittered uniformly
 * within [5 s, ceiling] so devices that failed together do not retry together.
 * Attempt 1 (or anything below it, or `NaN`) is exactly 5 s. There is no
 * attempt cap: a valid event is retried for as long as it takes.
 *
 * @param random a uniform draw in [0, 1]; out-of-range draws are clamped and a
 *   non-finite draw counts as 0 (the shortest wait).
 */
export function syncBackoffMs(attempt: number, random: () => number = Math.random): number {
  const nth = Number.isFinite(attempt) ? Math.max(1, Math.floor(attempt)) : 1;
  const ceiling = Math.min(OFFLINE_SYNC_BACKOFF_MAX_MS, OFFLINE_SYNC_BACKOFF_BASE_MS * 2 ** (nth - 1));
  const draw = random();
  const unit = Number.isFinite(draw) ? Math.min(1, Math.max(0, draw)) : 0;
  return Math.round(OFFLINE_SYNC_BACKOFF_BASE_MS + unit * (ceiling - OFFLINE_SYNC_BACKOFF_BASE_MS));
}
